import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Check, AlertCircle, Star } from 'lucide-react';
import { getPublicFormBySlug, submitPublicForm } from '../services/formService';
import { useFormTracking } from '../hooks/useFormTracking';
import type { FormWithFields, FormField, RatingFieldOptions } from '../types/forms';
import { logger } from '../lib/logger';

export function PublicForm() {
  const { slug } = useParams<{ slug: string }>();
  const [form, setForm] = useState<FormWithFields | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const hasStartedRef = useRef(false);

  const { trackStart } = useFormTracking({
    formId: form?.id || '',
    originPageType: null,
    originSlug: null,
    enabled: !!form,
  });

  useEffect(() => {
    if (slug) {
      loadForm();
    }
  }, [slug]);

  const loadForm = async () => {
    if (!slug) return;
    try {
      const data = await getPublicFormBySlug(slug);
      setForm(data);
      if (data) {
        const initialValues: Record<string, string> = {};
        data.fields.forEach((field) => {
          initialValues[field.id] = '';
        });
        setValues(initialValues);
      }
    } catch (error) {
      logger.error('Error loading form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (): boolean => {
    if (!form) return false;

    const errors: Record<string, string> = {};
    form.fields.forEach((field) => {
      const value = values[field.id] || '';

      if (field.required && !value.trim()) {
        errors[field.id] = 'Dieses Feld ist erforderlich';
      }

      if (field.type === 'EMAIL' && value.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors[field.id] = 'Bitte gib eine gültige E-Mail-Adresse ein';
        }
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await submitPublicForm(form, values);
      if (result.success) {
        setIsSubmitted(true);
      } else {
        setError('Es ist ein Fehler aufgetreten. Bitte versuche es erneut.');
      }
    } catch (err) {
      logger.error('Submission error:', err);
      setError('Es ist ein Fehler aufgetreten. Bitte versuche es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (fieldId: string, value: string) => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      trackStart();
    }
    setValues((prev) => ({ ...prev, [fieldId]: value }));
    if (fieldErrors[fieldId]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const renderField = (field: FormField) => {
    const value = values[field.id] || '';
    const fieldError = fieldErrors[field.id];

    const baseInputClass = `w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
      fieldError ? 'border-red-300 bg-red-50' : 'border-gray-200'
    }`;

    switch (field.type) {
      case 'TEXTAREA':
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            placeholder={field.placeholder || ''}
            rows={4}
            className={`${baseInputClass} resize-none`}
          />
        );

      case 'SELECT':
        return (
          <select
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            className={`${baseInputClass} bg-white`}
          >
            <option value="">{field.placeholder || 'Bitte auswählen...'}</option>
            {(Array.isArray(field.options) ? field.options : []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'MULTISELECT':
        return (
          <div className="space-y-2">
            {(Array.isArray(field.options) ? field.options : []).map((option) => {
              const selectedOptions = value ? value.split(',') : [];
              const isSelected = selectedOptions.includes(option);
              return (
                <label
                  key={option}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:border-primary transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      const newSelected = e.target.checked
                        ? [...selectedOptions, option]
                        : selectedOptions.filter((o) => o !== option);
                      handleChange(field.id, newSelected.join(','));
                    }}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              );
            })}
          </div>
        );

      case 'EMAIL':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            placeholder={field.placeholder || 'name@beispiel.de'}
            className={baseInputClass}
          />
        );

      case 'PHONE':
        return (
          <input
            type="tel"
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            placeholder={field.placeholder || '+49 123 456789'}
            className={baseInputClass}
          />
        );

      case 'RATING': {
        const ratingOptions = (field.options && typeof field.options === 'object' && !Array.isArray(field.options))
          ? field.options as RatingFieldOptions
          : {};
        const currentRating = parseInt(value) || 0;

        return (
          <div>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleChange(field.id, star.toString())}
                  className="p-1 focus:outline-none focus:ring-2 focus:ring-primary rounded transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-8 h-8 transition-colors ${
                      star <= currentRating
                        ? 'fill-amber-400 text-amber-400'
                        : 'fill-gray-200 text-gray-200 hover:fill-amber-200 hover:text-amber-200'
                    }`}
                  />
                </button>
              ))}
            </div>
            {(ratingOptions.label_low || ratingOptions.label_high) && (
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>{ratingOptions.label_low || ''}</span>
                <span>{ratingOptions.label_high || ''}</span>
              </div>
            )}
          </div>
        );
      }

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(field.id, e.target.value)}
            placeholder={field.placeholder || ''}
            className={baseInputClass}
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Formular nicht verfügbar
          </h1>
          <p className="text-gray-500">
            Dieses Formular existiert nicht oder ist nicht mehr verfügbar.
          </p>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-500" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Vielen Dank!
          </h1>
          <p className="text-gray-500">
            Deine Angaben wurden erfolgreich übermittelt.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-2xl border border-gray-100 p-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">{form.name}</h1>
            {form.description && (
              <p className="text-gray-500 mt-2">{form.description}</p>
            )}
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {form.fields.map((field) => (
              <div key={field.id}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {renderField(field)}
                {fieldErrors[field.id] && (
                  <p className="mt-1.5 text-sm text-red-500">{fieldErrors[field.id]}</p>
                )}
              </div>
            ))}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Wird gesendet...' : 'Absenden'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Powered by MAMAL
        </p>
      </div>
    </div>
  );
}
